import { Link } from "react-router-dom";
import { ArrowRight, Check, Home, Receipt, ShieldCheck, KeyRound, Wrench, PieChart, Briefcase } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useFetch, money } from "@/lib/api";
import { useAuth, homeFor } from "@/context/AuthContext";
import { cn } from "@/lib/utils";

const ICONS = [Receipt, ShieldCheck, KeyRound, Wrench, PieChart, Briefcase];

function Nav({ brand }) {
  const { session } = useAuth();
  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 border-b border-slate-200/80">
      <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="landing-logo">
          <div className="w-8 h-8 rounded-lg bg-emerald-700 flex items-center justify-center"><Home className="w-4 h-4 text-white" /></div>
          <span className="font-display text-xl font-extrabold">{brand}</span>
        </Link>
        <nav className="hidden md:flex items-center gap-8 text-sm text-slate-600">
          <a href="#features" className="hover:text-slate-900" data-testid="nav-features">Features</a>
          <Link to="/properties" className="hover:text-slate-900" data-testid="nav-find-property">Find a property</Link>
          <a href="#who" className="hover:text-slate-900" data-testid="nav-who">Who it's for</a>
          <a href="#pricing" className="hover:text-slate-900" data-testid="nav-pricing">Pricing</a>
          <a href="#faq" className="hover:text-slate-900" data-testid="nav-faq">FAQ</a>
        </nav>
        <div className="flex items-center gap-2">
          {session ? (
            <Link to={homeFor(session.user.role)} className="px-4 py-2 rounded-full bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800" data-testid="nav-dashboard">Open dashboard</Link>
          ) : (
            <>
              <Link to="/login" className="px-4 py-2 text-sm font-semibold text-slate-700 hover:text-slate-900" data-testid="nav-login">Log in</Link>
              <Link to="/register" className="px-4 py-2 rounded-full bg-emerald-700 text-white text-sm font-semibold hover:bg-emerald-800" data-testid="nav-register">Start free</Link>
            </>
          )}
        </div>
      </div>
    </header>
  );
}

function Hero({ site }) {
  return (
    <section className="relative overflow-hidden grain">
      <div className="max-w-7xl mx-auto px-6 pt-16 pb-24 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-6">
          <div className="eyebrow mb-5 rise" data-testid="hero-eyebrow">{site.hero_eyebrow}</div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] text-slate-900 rise rise-1" data-testid="hero-title">{site.hero_title}</h1>
          <p className="text-base sm:text-lg text-slate-600 mt-6 leading-relaxed max-w-xl rise rise-2" data-testid="hero-subtitle">{site.hero_subtitle}</p>
          <div className="flex flex-wrap gap-3 mt-10 rise rise-3">
            <Link to="/register" className="group inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-emerald-700 text-white font-semibold hover:bg-emerald-800 transition-colors" data-testid="hero-cta-register">
              Create free account <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </Link>
            <a href="#pricing" className="px-6 py-3.5 rounded-full border border-slate-300 font-semibold text-slate-800 hover:bg-white transition-colors" data-testid="hero-cta-pricing">See pricing</a>
          </div>
        </div>
        <div className="lg:col-span-6 relative rise rise-2">
          <div className="rounded-2xl overflow-hidden border border-slate-200 shadow-2xl shadow-slate-900/10 aspect-[4/3]">
            <img src={site.hero_image} alt="Rental property" className="w-full h-full object-cover" data-testid="hero-image" />
          </div>
          <div className="absolute -bottom-6 -left-4 sm:-left-8 bg-white border border-slate-200 rounded-xl p-4 shadow-xl w-56 rise rise-4">
            <div className="text-xs text-slate-500 uppercase tracking-wider">Rent collected</div>
            <div className="font-display text-2xl font-extrabold text-emerald-700">{money(18450)}</div>
            <div className="text-xs text-slate-500">this month · 96% on time</div>
          </div>
          <div className="absolute -top-5 -right-2 sm:-right-6 bg-white border border-slate-200 rounded-xl px-4 py-3 shadow-xl rise rise-4">
            <div className="flex items-center gap-2 text-sm font-semibold"><span className="w-2 h-2 rounded-full bg-amber-500" />Gas Safety due in 25 days</div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Features({ features }) {
  return (
    <section id="features" className="bg-white border-y border-slate-200 py-24">
      <div className="max-w-7xl mx-auto px-6">
        <div className="max-w-2xl mb-14">
          <div className="eyebrow mb-3">Features</div>
          <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight">Everything a rental needs, nothing it doesn't.</h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-200 border border-slate-200 rounded-2xl overflow-hidden">
          {(features || []).map((f, i) => {
            const Icon = ICONS[i % ICONS.length];
            return (
              <div key={i} className="bg-white p-8 hover:bg-slate-50 transition-colors" data-testid={`feature-${i}`}>
                <Icon className="w-6 h-6 text-emerald-700 mb-5" />
                <h3 className="text-xl font-bold">{f.title}</h3>
                <p className="text-sm text-slate-600 mt-2 leading-relaxed">{f.text}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

const WHO = [
  ["Landlords & owners", "Run your own portfolio: tenancies, rent, certificates and receipts in one place.", "https://images.unsplash.com/photo-1595846723416-99a641e1231a?crop=entropy&cs=srgb&fm=jpg&q=85&w=900"],
  ["Letting agents", "Manage properties for many landlords, with per-landlord statements and management fees.", "https://images.pexels.com/photos/10872771/pexels-photo-10872771.jpeg?auto=compress&cs=tinysrgb&w=900"],
  ["Renters", "A welcome pack with their own login to check rent, report repairs and download documents.", "https://images.pexels.com/photos/19673895/pexels-photo-19673895.jpeg?auto=compress&cs=tinysrgb&w=900"],
];

function Who() {
  return (
    <section id="who" className="py-24">
      <div className="max-w-7xl mx-auto px-6">
        <div className="eyebrow mb-3">Who it's for</div>
        <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight max-w-2xl mb-14">One platform, three very different days.</h2>
        <div className="grid md:grid-cols-3 gap-6">
          {WHO.map(([t, d, img], i) => (
            <div key={t} className={cn("group rounded-2xl overflow-hidden bg-white border border-slate-200 lift", i === 1 && "md:translate-y-8")} data-testid={`who-${i}`}>
              <div className="aspect-[4/3] overflow-hidden"><img src={img} alt={t} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" /></div>
              <div className="p-6">
                <h3 className="text-xl font-bold">{t}</h3>
                <p className="text-sm text-slate-600 mt-2">{d}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Pricing({ plans }) {
  return (
    <section id="pricing" className="py-24 bg-slate-900 text-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-400 mb-3">Pricing</div>
        <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight max-w-2xl mb-14">Simple monthly plans. Cancel any time.</h2>
        <div className="grid md:grid-cols-3 gap-6">
          {(plans || []).map((p) => (
            <div key={p.id} className={cn("rounded-2xl p-8 border flex flex-col", p.is_popular ? "bg-emerald-700 border-emerald-500" : "bg-slate-800/60 border-slate-700")} data-testid={`plan-card-${p.name}`}>
              <div className="flex items-center justify-between">
                <h3 className="text-2xl font-bold">{p.name}</h3>
                {p.is_popular && <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-white text-emerald-800">Most popular</span>}
              </div>
              <p className="text-sm text-slate-300 mt-2">{p.description}</p>
              <div className="mt-6 font-display text-4xl font-extrabold">{p.price_monthly > 0 ? money(p.price_monthly, p.currency) : "Free"}<span className="text-base font-medium text-slate-300">{p.price_monthly > 0 && " /month"}</span></div>
              <ul className="mt-6 space-y-2.5 text-sm flex-1">
                {p.features.map((f) => <li key={f} className="flex gap-2"><Check className="w-4 h-4 mt-0.5 text-emerald-300 shrink-0" />{f}</li>)}
              </ul>
              <Link to="/register" className={cn("mt-8 text-center py-3 rounded-full font-semibold transition-colors", p.is_popular ? "bg-white text-emerald-800 hover:bg-slate-100" : "bg-slate-700 hover:bg-slate-600")} data-testid={`plan-cta-${p.name}`}>
                Get started
              </Link>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function Landing() {
  const { data: site } = useFetch("/public/site");
  const { data: plans } = useFetch("/public/plans");
  if (!site) return <div className="min-h-screen bg-slate-50" />;
  return (
    <div className="bg-slate-50 text-slate-900" data-testid="landing-page">
      <Nav brand={site.brand_name} />
      <Hero site={site} />
      <Features features={site.features} />
      <Who />
      <Pricing plans={plans} />
      <section id="faq" className="py-24">
        <div className="max-w-3xl mx-auto px-6">
          <div className="eyebrow mb-3">FAQ</div>
          <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight mb-10">Questions, answered.</h2>
          <Accordion type="single" collapsible>
            {(site.faqs || []).map((f, i) => (
              <AccordionItem key={i} value={`f${i}`}>
                <AccordionTrigger className="text-left font-semibold" data-testid={`faq-${i}`}>{f.q}</AccordionTrigger>
                <AccordionContent className="text-slate-600">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>
      <footer className="border-t border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 py-10 flex flex-col md:flex-row justify-between gap-4 text-sm text-slate-500">
          <span data-testid="footer-text">{site.footer_text}</span>
          <a href={`mailto:${site.contact_email}`} className="hover:text-slate-900" data-testid="footer-contact">{site.contact_email}</a>
        </div>
      </footer>
    </div>
  );
}
